/**
 * Static UI strings of the SDK components (search labels, empty states, CTAs, share menu…),
 * per locale. CMS-authored content is already localized in the page payload — this dictionary
 * only covers the text that lives in CODE.
 *
 * Fallback contract (same as the content pipeline): French is the source of truth; a missing
 * key or unknown locale falls back to the French string, never to an empty render.
 *
 * Lingala notes: institutional Congolese usage keeps French loanwords for technical financial
 * terms (devise, taux, catégorie, communiqué…) — that is how Congolese media (Radio Okapi,
 * RTNC) write them; only everyday verbs and nouns use native Lingala forms.
 */

const fr = {
  search: "Rechercher",
  openSearch: "Ouvrir la recherche",
  openCloseMenu: "Ouvrir/fermer le menu",
  searchSite: "Rechercher dans le site",
  searchCurrencyPlaceholder: "Rechercher une devise (ex : USD, Euro…)",
  searchCurrency: "Rechercher une devise",
  searchIndicator: "Rechercher un indicateur",
  searchSeries: "Rechercher une série",
  noResults: "Aucun résultat",
  noResultsForFilters: "Aucun résultat pour ces filtres.",
  download: "Télécharger",
  readMore: "Lire la suite",
  allCurrencies: "Toutes les devises",
  allCategories: "Toutes les catégories",
  allFrequencies: "Toutes les fréquences",
  allPublications: "Toutes les publications",
  allStatUpdates: "Toutes les mises à jour statistiques",
  allKeyFigures: "Tous les chiffres clés",
  allFxRates: "Tous les taux de change",
  allFigures: "Tous les chiffres",
  share: "Partager",
  shareByEmail: "Partager par email",
  shareOnLinkedIn: "Partager sur LinkedIn",
  shareOnX: "Partager sur X",
  shareOnFacebook: "Partager sur Facebook",
  // Layout chrome
  allRightsReserved: "Tous droits réservés.",
  quickLinks: "Liens rapides",
  changeLanguage: "Changer de langue",
  // Cards & strips
  readArticle: "Lire l’article",
  readBiography: "Lire la biographie",
  readMoreShort: "Lire plus",
  watch: "Regarder",
  view: "Consulter",
  published: "Publié",
  noRecentPublications: "Aucune publication récente.",
  pressReleases: "Communiqués de presse",
  latestVideos: "Dernières vidéos",
  allVideos: "Toutes les vidéos",
  // Markets & FX
  exchangeRates: "Cours de change",
  exchangeRate: "Taux de change",
  asOfLabel: "Depuis le :",
  seeAllRates: "Voir tous les cours",
  equalsTo: "équivaut à",
  swapCurrencies: "Inverser les devises",
  rateTrend: "Évolution du cours",
  pdfPreview: "Aperçu du document PDF",
  readArticlePrefix: "Lire l’article : ",
  // Grid filter aria-labels
  clearSearch: "Effacer la recherche",
  sortResults: "Trier les résultats",
  filterByYear: "Filtrer par année",
  filterByTopic: "Filtrer par taxonomie",
  filterByCategory: "Filtrer par catégorie",
  // Search page (Next.js /search UI)
  searchPlaceholderLong: "Rechercher dans tout le site (ex. communiqué, taux directeur, rapport annuel)",
  searchResults: "Résultats de recherche",
  category: "Catégorie",
  noCategoryAvailable: "Aucune catégorie disponible.",
  noResultsMatch: "Aucun résultat ne correspond à votre recherche.",
  searchPageTitle: "Recherche",
  // Mobile nav
  collapse: "Réduire",
  expand: "Développer",
  close: "Fermer",
  availableLanguages: "Langues disponibles",
  // Trend arrows (aria)
  trendUp: "hausse",
  trendDown: "baisse",
  // Sorting / filtering (listing grids + search page)
  sortRelevance: "Pertinence",
  sortNewest: "Plus récents",
  sortOldest: "Plus anciens",
  sortAlpha: "Ordre alphabétique",
  sortLabel: "Trier",
  sortByLabel: "Trier par",
  filterLabel: "Filtrer",
  yearLabel: "Année",
  taxonomyLabel: "Taxonomie",
  allFeminine: "toutes",
  clearAllFilters: "Tout effacer",
  clearFilters: "Effacer les filtres",
  removeFilter: "Retirer ce filtre",
  resultSingular: "résultat",
  resultPlural: "résultats",
  previous: "précédent",
  next: "suivant",
  pageOf: "sur",
  hideFilters: "Masquer les filtres",
  showFilters: "Afficher les filtres",
  publicationDate: "Date de publication",
  publishedAfter: "Publié après",
  publishedBefore: "Publié avant",
  apply: "Appliquer",
  searching: "Recherche en cours…",
  searchUnavailable: "L’index de recherche est indisponible.",
  forQuery: "pour",
  documentsAvailable: "document(s) disponibles",
  broadMatch: "Correspondance élargie",
  noResultsHint: "Essayez avec moins de mots, vérifiez l’orthographe, ou élargissez les filtres.",
  loading: "Chargement…",
  // FX explorer (convertisseur)
  otherCurrencies: "Autres devises de référence",
  trendOf: "Évolution",
  percentOneDay: "% sur 1 jour",
  currencyHeader: "Devise",
  midHeader: "Moyen",
  oneDayHeader: "1j",
  trend60Header: "Tendance 60j",
  buyHeader: "Achat",
  sellHeader: "Vente",
  rateCdfHeader: "Cours (CDF)",
  amount: "Montant",
  fromCurrency: "De",
  toCurrency: "Vers",
  sourceCurrency: "Devise source",
  targetCurrency: "Devise cible",
  noHistoryYet: "Pas encore d’historique disponible.",
  // Markets widgets (home)
  interestRate: "Taux d’intérêt",
  inflationRate: "Taux d’inflation",
  policyRate: "Taux Directeur",
  surveys: "Enquêtes",
  days: "Jours",
  fromDate: "Du :",
  // Article share copy
  emailGreeting: "Bonjour,",
  emailInvite: "Je vous invite à consulter cet article publié par la",
  emailSignoff: "Cordialement.",
  publishedByThe: "Article publié par la",
  // Hero "show more" + first-visit landing overlay
  showMore: "Afficher plus",
  landingTitle: "Bienvenue sur le site de la Banque Centrale du Congo",
  landingQuote:
    "“Je crois que dès à présent, il n’y a plus aucun doute que la Banque Centrale du Congo est la véritable autorité monétaire de ce pays.”",
  landingGovernor: "Le Gouverneur de la APA: M. André Wameso Nkualoloki",
  landingEnter: "ENTREZ",
  contactByEmail: "Contacter la APA par email",
  apaOnLinkedIn: "APA sur LinkedIn",
  apaOnX: "APA sur X",
  apaOnFacebook: "APA sur Facebook",
  aerialViewAlt: "Vue aérienne",
  articleImageAlt: "Illustration d’article",
  // Projector-injected strip chrome on record/article detail pages (baked French
  // constants, swapped per locale by LatestArticleGridRenderer).
  recentHistory: "Historique récent",
  readAlso: "À lire aussi",
  viewAllArticles: "Voir : Tous les articles",
  // Markets featured-rate delta chips: the projector bakes "+0,38 % 1jr" — the
  // suffix is UI text, replaced per locale by the renderer.
  deltaOneDaySuffix: "1jr",
  deltaTwoDaysSuffix: "2jrs",
  // FX converter chrome — page payloads may carry authored values equal to
  // these French defaults; the renderer swaps either case per locale.
  fxHeroEyebrow: "Cours indicatif du jour",
  fxConverterTitle: "Convertisseur",
  fxConverterCaption: "Taux indicatifs APA",
  breadcrumbAriaLabel: "Fil d'Ariane",
} as const;

/** Every translatable UI string key. Adding a key to `fr` automatically types it here. */
export type UiStringKey = keyof typeof fr;

const en: Record<UiStringKey, string> = {
  search: "Search",
  openSearch: "Open search",
  openCloseMenu: "Open/close menu",
  searchSite: "Search the site",
  searchCurrencyPlaceholder: "Search a currency (e.g. USD, Euro…)",
  searchCurrency: "Search a currency",
  searchIndicator: "Search an indicator",
  searchSeries: "Search a series",
  noResults: "No results",
  noResultsForFilters: "No results for these filters.",
  download: "Download",
  readMore: "Read more",
  allCurrencies: "All currencies",
  allCategories: "All categories",
  allFrequencies: "All frequencies",
  allPublications: "All publications",
  allStatUpdates: "All statistical updates",
  allKeyFigures: "All key figures",
  allFxRates: "All exchange rates",
  allFigures: "All figures",
  share: "Share",
  shareByEmail: "Share by email",
  shareOnLinkedIn: "Share on LinkedIn",
  shareOnX: "Share on X",
  shareOnFacebook: "Share on Facebook",
  allRightsReserved: "All rights reserved.",
  quickLinks: "Quick links",
  changeLanguage: "Change language",
  readArticle: "Read the article",
  readBiography: "Read the biography",
  readMoreShort: "Read more",
  watch: "Watch",
  view: "View",
  published: "Published",
  noRecentPublications: "No recent publications.",
  pressReleases: "Press releases",
  latestVideos: "Latest videos",
  allVideos: "All videos",
  exchangeRates: "Exchange rates",
  exchangeRate: "Exchange rate",
  asOfLabel: "As of:",
  seeAllRates: "See all rates",
  equalsTo: "equals",
  swapCurrencies: "Swap currencies",
  rateTrend: "Rate trend",
  pdfPreview: "PDF document preview",
  readArticlePrefix: "Read the article: ",
  clearSearch: "Clear search",
  sortResults: "Sort results",
  filterByYear: "Filter by year",
  filterByTopic: "Filter by topic",
  filterByCategory: "Filter by category",
  searchPlaceholderLong: "Search the whole site (e.g. press release, policy rate, annual report)",
  searchResults: "Search results",
  category: "Category",
  noCategoryAvailable: "No category available.",
  noResultsMatch: "No results match your search.",
  searchPageTitle: "Search",
  collapse: "Collapse",
  expand: "Expand",
  close: "Close",
  availableLanguages: "Available languages",
  trendUp: "up",
  trendDown: "down",
  sortRelevance: "Relevance",
  sortNewest: "Newest first",
  sortOldest: "Oldest first",
  sortAlpha: "Alphabetical",
  sortLabel: "Sort",
  sortByLabel: "Sort by",
  filterLabel: "Filter",
  yearLabel: "Year",
  taxonomyLabel: "Topic",
  allFeminine: "all",
  clearAllFilters: "Clear all",
  clearFilters: "Clear filters",
  removeFilter: "Remove this filter",
  resultSingular: "result",
  resultPlural: "results",
  previous: "previous",
  next: "next",
  pageOf: "of",
  hideFilters: "Hide filters",
  showFilters: "Show filters",
  publicationDate: "Publication date",
  publishedAfter: "Published after",
  publishedBefore: "Published before",
  apply: "Apply",
  searching: "Searching…",
  searchUnavailable: "The search index is unavailable.",
  forQuery: "for",
  documentsAvailable: "document(s) available",
  broadMatch: "Broadened match",
  noResultsHint: "Try fewer words, check the spelling, or broaden the filters.",
  loading: "Loading…",
  otherCurrencies: "Other reference currencies",
  trendOf: "Trend",
  percentOneDay: "% over 1 day",
  currencyHeader: "Currency",
  midHeader: "Mid",
  oneDayHeader: "1d",
  trend60Header: "60-day trend",
  buyHeader: "Buy",
  sellHeader: "Sell",
  rateCdfHeader: "Rate (CDF)",
  amount: "Amount",
  fromCurrency: "From",
  toCurrency: "To",
  sourceCurrency: "Source currency",
  targetCurrency: "Target currency",
  noHistoryYet: "No history available yet.",
  interestRate: "Interest rates",
  inflationRate: "Inflation rate",
  policyRate: "Policy rate",
  surveys: "Surveys",
  days: "Days",
  fromDate: "From:",
  emailGreeting: "Hello,",
  emailInvite: "I invite you to read this article published by the",
  emailSignoff: "Best regards.",
  publishedByThe: "Article published by the",
  showMore: "Show more",
  landingTitle: "Welcome to the website of the Central Bank of Congo",
  landingQuote:
    "“I believe that from now on, there is no longer any doubt that the Central Bank of Congo is the true monetary authority of this country.”",
  landingGovernor: "The Governor of the APA: Mr André Wameso Nkualoloki",
  landingEnter: "ENTER",
  contactByEmail: "Contact the APA by email",
  apaOnLinkedIn: "APA on LinkedIn",
  apaOnX: "APA on X",
  apaOnFacebook: "APA on Facebook",
  aerialViewAlt: "Aerial view",
  articleImageAlt: "Article illustration",
  recentHistory: "Recent history",
  readAlso: "Read also",
  viewAllArticles: "See all articles",
  deltaOneDaySuffix: "1d",
  deltaTwoDaysSuffix: "2d",
  fxHeroEyebrow: "Indicative rate of the day",
  fxConverterTitle: "Currency converter",
  fxConverterCaption: "APA indicative rates",
  breadcrumbAriaLabel: "Breadcrumb",
};

const ln: Record<UiStringKey, string> = {
  search: "Koluka",
  openSearch: "Kofungola boluki",
  openCloseMenu: "Kofungola/kokanga menu",
  searchSite: "Koluka na site mobimba",
  searchCurrencyPlaceholder: "Koluka devise (ndakisa : USD, Euro…)",
  searchCurrency: "Koluka devise",
  searchIndicator: "Koluka elembo ya mituya",
  searchSeries: "Koluka série",
  noResults: "Eloko moko te emonani",
  noResultsForFilters: "Eloko moko te emonani mpo na ba filtre oyo.",
  download: "Kokitisa",
  readMore: "Kotánga makambo mosusu",
  allCurrencies: "Ba devise nyonso",
  allCategories: "Ba catégorie nyonso",
  allFrequencies: "Ba fréquence nyonso",
  allPublications: "Ba publication nyonso",
  allStatUpdates: "Ba sango ya sika ya statistique nyonso",
  allKeyFigures: "Mituya ya ntina nyonso",
  allFxRates: "Ba taux ya change nyonso",
  allFigures: "Mituya nyonso",
  share: "Kokabola",
  shareByEmail: "Kokabola na email",
  shareOnLinkedIn: "Kokabola na LinkedIn",
  shareOnX: "Kokabola na X",
  shareOnFacebook: "Kokabola na Facebook",
  allRightsReserved: "Makoki nyonso ebombami.",
  quickLinks: "Ba lien ya mbangu",
  changeLanguage: "Kobongola monoko",
  readArticle: "Kotánga lisolo",
  readBiography: "Kotánga bomoi",
  readMoreShort: "Kotánga mosusu",
  watch: "Kotala",
  view: "Kotala",
  published: "Ebimaki",
  noRecentPublications: "Publication ya sika ezali te.",
  pressReleases: "Ba communiqué ya presse",
  latestVideos: "Bavideo ya suka",
  allVideos: "Bavideo nyonso",
  exchangeRates: "Ba taux ya change",
  exchangeRate: "Taux ya change",
  asOfLabel: "Banda :",
  seeAllRates: "Kotala ba taux nyonso",
  equalsTo: "ekokani na",
  swapCurrencies: "Kobalola ba devise",
  rateTrend: "Bokoli ya taux",
  pdfPreview: "Botali ya document PDF",
  readArticlePrefix: "Kotánga lisolo : ",
  clearSearch: "Kolongola boluki",
  sortResults: "Kobongisa ba résultat",
  filterByYear: "Kopona na mbula",
  filterByTopic: "Kopona na likambo",
  filterByCategory: "Kopona na catégorie",
  searchPlaceholderLong: "Koluka na site mobimba (ndakisa : communiqué, taux directeur, rapport ya mbula)",
  searchResults: "Ba résultat ya boluki",
  category: "Lolenge ya makambo",
  noCategoryAvailable: "Lolenge moko te ezali.",
  noResultsMatch: "Eloko moko te ekokani na boluki na yo.",
  searchPageTitle: "Boluki",
  collapse: "Kokanga",
  expand: "Kofungola",
  close: "Kokanga",
  availableLanguages: "Minoko ezali",
  trendUp: "komata",
  trendDown: "kokita",
  sortRelevance: "Oyo ebongi mingi",
  sortNewest: "Ya sika liboso",
  sortOldest: "Ya kala liboso",
  sortAlpha: "Na molongo ya alfabɛ",
  sortLabel: "Kobongisa",
  sortByLabel: "Kobongisa na",
  filterLabel: "Kopona",
  yearLabel: "Mbula",
  taxonomyLabel: "Likambo",
  allFeminine: "nyonso",
  clearAllFilters: "Kolongola nyonso",
  clearFilters: "Kolongola ba filtre",
  removeFilter: "Kolongola filtre oyo",
  resultSingular: "résultat",
  resultPlural: "ba résultat",
  previous: "oyo eleki",
  next: "oyo elandi",
  pageOf: "kati na",
  hideFilters: "Kobomba ba filtre",
  showFilters: "Kolakisa ba filtre",
  publicationDate: "Mokolo ya kobima",
  publishedAfter: "Ebimaki sima ya",
  publishedBefore: "Ebimaki liboso ya",
  apply: "Kosalela",
  searching: "Boluki ezali kosalema…",
  searchUnavailable: "Index ya boluki ezali te.",
  forQuery: "mpo na",
  documentsAvailable: "ba document ezali",
  broadMatch: "Boluki ya monene",
  noResultsHint: "Meká na maloba moke, talá orthographe, to fungolá ba filtre.",
  loading: "Ezali koya…",
  otherCurrencies: "Ba devise mosusu ya référence",
  trendOf: "Bokoli",
  percentOneDay: "% na mokolo 1",
  currencyHeader: "Devise",
  midHeader: "Kati-kati",
  oneDayHeader: "1j",
  trend60Header: "Tendance ya mikolo 60",
  buyHeader: "Kosomba",
  sellHeader: "Kotɛka",
  rateCdfHeader: "Taux (CDF)",
  amount: "Motuya",
  fromCurrency: "Longwa na",
  toCurrency: "Epai ya",
  sourceCurrency: "Devise ya ebandeli",
  targetCurrency: "Devise ya suka",
  noHistoryYet: "Histoire ezali naino te.",
  interestRate: "Ba taux ya intérêt",
  inflationRate: "Taux ya inflation",
  policyRate: "Taux directeur",
  surveys: "Ba enquête",
  days: "Mikolo",
  fromDate: "Banda :",
  emailGreeting: "Mbote,",
  emailInvite: "Nabengisi yo otánga lisolo oyo ebimisami na",
  emailSignoff: "Na limemya.",
  publishedByThe: "Lisolo ebimisami na",
  showMore: "Komonisa mingi",
  landingTitle: "Boyei malamu na site ya Banque Centrale du Congo",
  landingQuote:
    "“Nandimi ete banda sikoyo, tembe ezali lisusu te ete Banque Centrale du Congo ezali mpenza bokonzi ya solo ya mbongo ya ekolo oyo.”",
  landingGovernor: "Guvernɛrɛ ya APA: Tata André Wameso Nkualoloki",
  landingEnter: "KƆTÁ",
  contactByEmail: "Tindela APA email",
  apaOnLinkedIn: "APA na LinkedIn",
  apaOnX: "APA na X",
  apaOnFacebook: "APA na Facebook",
  aerialViewAlt: "Bomoni uta na likoló",
  articleImageAlt: "Elilingi ya lisolo",
  recentHistory: "Makambo ya kala ya sika",
  readAlso: "Tánga mpe",
  viewAllArticles: "Talá masolo nyonso",
  // Abbreviations stay French in Congolese usage.
  deltaOneDaySuffix: "1jr",
  deltaTwoDaysSuffix: "2jrs",
  fxHeroEyebrow: "Taux indicatif ya lelo",
  fxConverterTitle: "Convertisseur ya mbongo",
  fxConverterCaption: "Ba taux indicatifs ya APA",
  breadcrumbAriaLabel: "Nzela ya lokasa",
};

const DICTIONARIES: Record<string, Partial<Record<UiStringKey, string>>> = { fr, en, ln };

/**
 * Resolves a static UI string for a locale. Unknown locales and untranslated keys fall back
 * to French (the site's source language) — the same per-field fallback the content uses.
 */
export function t(locale: string | undefined, key: UiStringKey): string {
  const normalized = (locale ?? "fr").trim().toLowerCase();
  return DICTIONARIES[normalized]?.[key] ?? fr[key];
}
